import { useCallback, useEffect, useState } from 'react';
import { ShoppingCart, ChevronDown, ChevronRight } from 'lucide-react';
import Button from '../ui/Button';
import purchaseOrderService, { type PurchaseOrderPerson } from '../../services/purchaseOrders';
import { errorMessage } from '../../utils/apiResponse';

/**
 * Organisation Admin → Purchase orders: who in the organisation can raise,
 * send and receive purchase orders. Ticking someone puts the Purchase Orders
 * card on their Dashboard. No licence seat is involved: this list is the
 * whole of it, and the server holds it (`purchase_order_users`).
 *
 * Accounts seats read purchase orders whether or not they are ticked here,
 * which the card says, so an unticked accountant is not a surprise.
 */

const nameOf = (p: PurchaseOrderPerson) => [p.first_name, p.last_name].filter(Boolean).join(' ') || p.email;

function PurchaseOrderUsers() {
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState<PurchaseOrderPerson[]>([]);
  const [chosen, setChosen] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const show = (list: PurchaseOrderPerson[]) => {
    setPeople(list);
    setChosen(new Set(list.filter((p) => p.authorised).map((p) => p.id)));
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await purchaseOrderService.getPeople();
      if (res.success && res.data) show(res.data);
      else setError(errorMessage(res, 'Could not load who is authorised'));
    } catch (e) {
      setError(errorMessage(e, 'Could not load who is authorised'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (id: number) => {
    setSaved(false);
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await purchaseOrderService.setAuthorised([...chosen]);
      if (res.success && res.data) {
        show(res.data);
        setSaved(true);
      } else {
        setError(errorMessage(res, 'Could not save who is authorised'));
      }
    } catch (e) {
      setError(errorMessage(e, 'Could not save who is authorised'));
    } finally {
      setSaving(false);
    }
  };

  const authorisedNow = people.filter((p) => p.authorised).length;
  const changed = people.some((p) => p.authorised !== chosen.has(p.id));

  return (
    <div>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 bg-white border border-neutral-200 rounded-xl p-6 hover:border-indigo-300 hover:shadow-sm transition-all text-left"
      >
        <div className="flex items-center gap-4 min-w-0">
          <div className="bg-indigo-100 p-3 rounded-lg shrink-0">
            <ShoppingCart className="h-6 w-6 text-indigo-600" />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-neutral-900 break-words">Purchase orders</h3>
            <p className="text-sm text-neutral-500 break-words">
              Choose who can raise purchase orders.{' '}
              {!loading && !error && (authorisedNow === 0
                ? 'Nobody is authorised yet.'
                : `${authorisedNow} ${authorisedNow === 1 ? 'person is' : 'people are'} authorised.`)}
            </p>
          </div>
        </div>
        {open
          ? <ChevronDown className="h-5 w-5 text-neutral-400 shrink-0" />
          : <ChevronRight className="h-5 w-5 text-neutral-400 shrink-0" />}
      </button>

      {open && (
        <div className="mt-4 bg-white border border-neutral-200 rounded-xl p-6">
          <p className="text-sm text-neutral-600 break-words">
            Someone ticked here gets the Purchase Orders card on their Dashboard and can raise an order for a
            farm, send it to the supplier, receive it and cancel it. Nobody else can, whatever licence seats
            they hold. Anyone with an Accounts seat can read purchase orders without being ticked.
          </p>

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>
          )}

          {loading ? (
            <div className="mt-4 h-24 bg-neutral-100 rounded-lg animate-pulse" />
          ) : people.length > 0 && (
            <>
              <ul className="mt-4 divide-y divide-neutral-100 border border-neutral-200 rounded-lg">
                {people.map((p) => (
                  <li key={p.id}>
                    <label className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-neutral-50">
                      <input
                        type="checkbox"
                        checked={chosen.has(p.id)}
                        onChange={() => toggle(p.id)}
                        className="mt-1 h-4 w-4 rounded border-neutral-300 text-indigo-600 shrink-0"
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-neutral-900 break-words">{nameOf(p)}</span>
                        <span className="block text-xs text-neutral-500 break-all">{p.email}</span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button variant="primary" size="sm" onClick={save} disabled={!changed || saving}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
                {saved && !changed && <span className="text-sm text-green-700">Saved.</span>}
                {changed && <span className="text-sm text-neutral-500">{chosen.size} ticked, not saved yet.</span>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default PurchaseOrderUsers;
